import React, { useState, useEffect } from 'react'
import { 
  Card, CardBody, CardHeader, Table, Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Alert, Spinner, InputGroup, InputGroupAddon, InputGroupText,
  Pagination, PaginationItem, PaginationLink
} from 'reactstrap';
import ApiService from '../services/ApiService';
import PublicIdDisplay from '../components/PublicIdDisplay';
import { useHistory } from 'react-router-dom';

const EventsPanel = () => {
  const history = useHistory();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [alert, setAlert] = useState({ type: '', message: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [allTenants, setAllTenants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(15);
  
  const [formData, setFormData] = useState({
    tenant_id: '',
    category: '',
    class: '',
    title: '',
    city: '',
    country: '',
    starts_at: '',
    ends_at: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      await fetchEvents();
      await fetchCategories();
      await fetchTenants();
      await fetchAllTenants();
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, categoryFilter, tenantFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: perPage,
        ...(searchTerm && { search: searchTerm }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(tenantFilter && { tenant_id: tenantFilter })
      };
      const response = await ApiService.getAdminEvents(params);
      setEvents(response.data.data || response.data);
      setTotalPages(response.data.last_page || 1);
      setCurrentPage(response.data.current_page || 1);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching events:", error);
      setAlert({ type: 'danger', message: 'Failed to fetch events' });
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await ApiService.getEventCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await ApiService.getEventTenants();
      setTenants(response.data);
    } catch (error) {
      console.error("Error fetching tenants:", error);
    }
  };

  const fetchAllTenants = async () => {
    try {
      const response = await ApiService.getAllTenants();
      setAllTenants(response.data);
    } catch (error) {
      console.error("Error fetching all tenants:", error);
    }
  };

  const handleViewBroadcasts = (event) => {
    // Navigate to TV Broadcasts tab with the event pre-selected
    history.push('/admin/broadcasts', { eventId: event.public_id });
  };

  const handleCreateEvent = () => {
    setFormData({
      tenant_id: '',
      category: '',
      class: '',
      title: '',
      city: '',
      country: '',
      starts_at: '',
      ends_at: ''
    });
    setFormErrors({});
    setSelectedEvent(null);
    setEditModal(true);
  };

  const handleEditEvent = (event) => {
    setFormData({
      tenant_id: event.tenant_id || '',
      category: event.category || '',
      class: event.class || '',
      title: event.title || '',
      city: event.city || '',
      country: event.country || '',
      starts_at: event.starts_at ? new Date(event.starts_at).toISOString().slice(0, 16) : '',
      ends_at: event.ends_at ? new Date(event.ends_at).toISOString().slice(0, 16) : ''
    });
    setFormErrors({});
    setSelectedEvent(event);
    setEditModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    
    try {
      setIsSubmitting(true);
      await ApiService.deleteEvent(eventToDelete.public_id);
      setAlert({ type: 'success', message: 'Event deleted successfully' });
      setDeleteModal(false);
      setEventToDelete(null);
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      const errorMessage = error.response?.data?.message || 'Failed to delete event';
      setAlert({ type: 'danger', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title) errors.title = 'Title is required';
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.starts_at) errors.starts_at = 'Start date is required';
    if (!formData.ends_at) errors.ends_at = 'End date is required';
    if (formData.starts_at && formData.ends_at && new Date(formData.starts_at) >= new Date(formData.ends_at)) {
      errors.ends_at = 'End date must be after start date';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (selectedEvent) {
        await ApiService.updateEvent(selectedEvent.public_id, formData);
        setAlert({ type: 'success', message: 'Event updated successfully' });
      } else {
        await ApiService.createEvent(formData);
        setAlert({ type: 'success', message: 'Event created successfully' });
      }
      setEditModal(false);
      fetchEvents();
    } catch (error) {
      console.error("Error saving event:", error);
      const errorMessage = error.response?.data?.message || 'Failed to save event';
      setAlert({ type: 'danger', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeEditModal = () => {
    setEditModal(false);
    setSelectedEvent(null);
    setFormData({
      tenant_id: '',
      category: '',
      class: '',
      title: '',
      city: '',
      country: '',
      starts_at: '',
      ends_at: ''
    });
    setFormErrors({});
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <>
      <div className="container-fluid">
        {alert.message && (
          <Alert color={alert.type} toggle={() => setAlert({ type: '', message: '' })}>
            {alert.message}
          </Alert>
        )}
        
        <Row className="mb-4">
          <Col xs={12}>
            <Card className="admin-card">
              <CardHeader className="admin-card-header d-flex justify-content-between align-items-center">
                <h4 className="admin-card-title mb-0">Events</h4>
                <Button color="primary" onClick={handleCreateEvent}>
                  <i className="fas fa-plus mr-2"></i>Create Event
                </Button>
              </CardHeader>
              <CardBody>
                <Row className="mb-3">
                  <Col md={4}>
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText><i className="fas fa-search"></i></InputGroupText>
                      </InputGroupAddon>
                      <Input
                        type="text"
                        placeholder="Search events..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Input
                      type="select"
                      value={tenantFilter}
                      onChange={(e) => setTenantFilter(e.target.value)}
                    >
                      <option value="">All Tenants</option>
                      {tenants.map(tenant => (
                        <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Input
                      type="select"
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </Input>
                  </Col>
                </Row>

                {loading ? (
                  <div className="text-center p-4">
                    <Spinner color="primary" />
                    <p>Loading events...</p>
                  </div>
                ) : (
                  <>
                    <Table responsive hover className="table-centered mb-0">
                      <thead className="admin-table-header">
                        <tr>
                          <th>ID</th>
                          <th>Title</th>
                          <th>Tenant</th>
                          <th>Category</th>
                          <th>Class</th>
                          <th>City/Country</th>
                          <th>Time Period</th>
                          <th>TV Broadcasts</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center">No events found</td>
                          </tr>
                        ) : (
                          events.map((event) => (
                            <tr key={event.public_id}>
                              <td><PublicIdDisplay publicId={event.public_id} /></td>
                              <td>{event.title}</td>
                              <td>{event.tenant ? event.tenant.name : 'N/A'}</td>
                              <td>{event.category}</td>
                              <td>{event.class || 'N/A'}</td>
                              <td>{event.city && event.country ? `${event.city}, ${event.country}` : 'N/A'}</td>
                              <td>
                                {formatDate(event.starts_at)} - {formatDate(event.ends_at)}
                              </td>
                              <td>{event.tv_broadcasts_count || 0}</td>
                              <td>
                                <div className="btn-group" role="group">
                                  <Button 
                                    color="secondary" 
                                    outline
                                    size="sm" 
                                    onClick={() => handleViewBroadcasts(event)}
                                    className="mr-1"
                                    title="View Broadcasts"
                                  >
                                    <i className="fas fa-tv"></i>
                                  </Button>
                                  <Button 
                                    color="secondary" 
                                    outline
                                    size="sm" 
                                    onClick={() => handleEditEvent(event)}
                                    title="Edit Event"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                    
                    {totalPages > 1 && (
                      <Pagination className="mt-3 justify-content-center">
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                        </PaginationItem>
                        {[...Array(totalPages)].map((_, i) => (
                          <PaginationItem key={i + 1} active={currentPage === i + 1}>
                            <PaginationLink onClick={() => setCurrentPage(i + 1)}>
                              {i + 1}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem disabled={currentPage === totalPages}>
                          <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                        </PaginationItem>
                      </Pagination>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Edit/Create Modal */}
      <Modal isOpen={editModal} toggle={closeEditModal} size="lg">
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={closeEditModal}>
            {selectedEvent ? 'Edit Event' : 'Create New Event'}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={8}>
                <FormGroup>
                  <Label for="title">Title *</Label>
                  <Input
                    type="text"
                    name="title"
                    id="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    invalid={!!formErrors.title}
                  />
                  {formErrors.title && <div className="invalid-feedback">{formErrors.title}</div>}
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="tenant_id">Tenant</Label>
                  <Input
                    type="select"
                    name="tenant_id"
                    id="tenant_id"
                    value={formData.tenant_id}
                    onChange={handleFormChange}
                    invalid={!!formErrors.tenant_id}
                  >
                    <option value="">Select Tenant</option>
                    {allTenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                    ))}
                  </Input>
                  {formErrors.tenant_id && <div className="invalid-feedback">{formErrors.tenant_id}</div>}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="category">Category *</Label>
                  <Input
                    type="text"
                    name="category"
                    id="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    invalid={!!formErrors.category}
                  />
                  {formErrors.category && <div className="invalid-feedback">{formErrors.category}</div>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="class">Class</Label>
                  <Input
                    type="text"
                    name="class"
                    id="class"
                    value={formData.class}
                    onChange={handleFormChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="city">City</Label>
                  <Input
                    type="text"
                    name="city"
                    id="city"
                    value={formData.city}
                    onChange={handleFormChange}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="country">Country</Label>
                  <Input
                    type="text"
                    name="country"
                    id="country"
                    value={formData.country}
                    onChange={handleFormChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="starts_at">Start Date *</Label>
                  <Input
                    type="datetime-local"
                    name="starts_at"
                    id="starts_at"
                    value={formData.starts_at}
                    onChange={handleFormChange}
                    invalid={!!formErrors.starts_at}
                  />
                  {formErrors.starts_at && <div className="invalid-feedback">{formErrors.starts_at}</div>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="ends_at">End Date *</Label>
                  <Input
                    type="datetime-local"
                    name="ends_at"
                    id="ends_at"
                    value={formData.ends_at}
                    onChange={handleFormChange}
                    invalid={!!formErrors.ends_at}
                  />
                  {formErrors.ends_at && <div className="invalid-feedback">{formErrors.ends_at}</div>}
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter className="d-flex justify-content-between">
            <Button color="secondary" onClick={closeEditModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
              {selectedEvent ? 'Update Event' : 'Create Event'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete the event "{eventToDelete?.title}"? This action cannot be undone.
          {eventToDelete?.tv_broadcasts_count > 0 && (
            <Alert color="warning" className="mt-3">
              This event has {eventToDelete.tv_broadcasts_count} TV broadcast(s). They must be removed first.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button color="secondary" onClick={() => setDeleteModal(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteConfirm} disabled={isSubmitting}>
            {isSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default EventsPanel;